/* eslint-disable react/jsx-curly-newline */
import React, { useEffect, useState } from 'react';
import { Cookies } from 'react-cookie';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { localize, pluralizeString } from '@saviynt/common';
import {
  Box,
  Button,
  ButtonIcon,
  Checkbox,
  Chip,
  Color,
  Icon,
  Illustration,
  InlineMessage,
  Loader,
  MobileDrawer,
  ModalIsland,
  Popover,
  Typography,
} from '@saviynt/design-system';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import {
  applySaviCompanionActions,
  getSaviCompanionStatus,
  setCertTriggerFromSearchOrFilter,
} from '../../../redux/certdetails/action';
import {
  CERTIFICATION_PREVEIW_STATUS_CODE,
  SCREEN_SIZE_MOBILE_XLARGE,
  SCREEN_WIDTH_QUERIES,
} from '../../../utils/constant';
import { useInvertedLocalize } from '../../../utils/localization';
import { certsFormattedMessage, getMessageKey } from '../../../utils/utils';

import { MAX_OFFSET } from './constants';
import invertedMessages from './messages';

import './SaviCompanion.css';

const cookies = new Cookies();

function SaviCompanionLandingBody({ onGotItClick }) {
  const intl = useIntl();
  const { messages } = useSelector((state) => state?.user);

  return (
    <div className='Savi-Landing-Body'>
      <div>
        <Typography kind='body2' className='Text'>
          {messages.CERTS_SAVI_INTRO_MSG ||
            localize(intl, getMessageKey('CERTS_SAVI_INTRO_MSG'))}
        </Typography>
      </div>
      <div className='Landing-buttons-wrapper'>
        <Button
          type='button'
          kind='filled'
          size='large'
          className='Landing-buttons'
          onClick={onGotItClick}>
          <Typography kind='body2-bold' className='Text'>
            {messages.CERTS_SAVI_GOT_IT ||
              localize(intl, getMessageKey('CERTS_SAVI_GOT_IT'))}
          </Typography>
        </Button>
      </div>
    </div>
  );
}

SaviCompanionLandingBody.propTypes = {
  onGotItClick: PropTypes.func.isRequired,
};

function QuestionHeader({
  isOptionSelected,
  minimiseReason,
  title,
  setIsOptionSelected,
}) {
  const { mediaQueryValue } = useSelector(
    (state) => state?.certifications?.details?.global
  );

  const mobileXlargeQuery = SCREEN_WIDTH_QUERIES.find(
    (item) => item.label === SCREEN_SIZE_MOBILE_XLARGE
  );
  const isMobileXlargeWidth = mobileXlargeQuery.value <= mediaQueryValue;

  return (
    <div className='Question-Header-Wrapper-Minimised'>
      {isOptionSelected ? (
        <div
          className='Question-Header-Wrapper'
          role='button'
          tabIndex={0}
          onClick={() => setIsOptionSelected(!isOptionSelected)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              setIsOptionSelected(!isOptionSelected);
            }
          }}>
          {isMobileXlargeWidth && (
            <Icon kind='ChevronLeft' size='medium' color='navy-900' />
          )}
          {!isMobileXlargeWidth && (
            <Icon kind='SaviCompanionGradient' size='medium' />
          )}
          <div className='Minimised'>
            <div>
              <Typography kind='body2' className='Question-Header'>
                {title}
              </Typography>
              <Typography
                kind='body1'
                className='Question-Header Question-Header-Reason'>
                <Color color='navy-900'>{minimiseReason}</Color>
              </Typography>
            </div>
            {!isMobileXlargeWidth && (
              <Icon kind='Edit' size='medium' color='navy-900' />
            )}
          </div>
        </div>
      ) : (
        <div
          className={`Question-Header-Wrapper ${
            minimiseReason ? ' Question-Header-Wrapper-Minimised' : ''
          }`}
          role='button'
          tabIndex={-1}
          onClick={() =>
            minimiseReason &&
            setIsOptionSelected &&
            setIsOptionSelected(!isOptionSelected)
          }
          onKeyDown={(e) =>
            e.key === 'Enter' &&
            minimiseReason &&
            setIsOptionSelected &&
            setIsOptionSelected(!isOptionSelected)
          }>
          <Icon kind='SaviCompanionGradient' size='medium' />
          <Typography kind='h3' className='Question-Header'>
            {title}
          </Typography>
        </div>
      )}
    </div>
  );
}

QuestionHeader.propTypes = {
  isOptionSelected: PropTypes.bool.isRequired,
  minimiseReason: PropTypes.string,
  title: PropTypes.string.isRequired,
  setIsOptionSelected: PropTypes.func.isRequired,
};

QuestionHeader.defaultProps = {
  minimiseReason: '',
};

function SaviStaticOption({
  id,
  label,
  description,
  iconKind,
  chipLabel,
  onClick,
  tabIndex,
}) {
  const { mediaQueryValue } = useSelector(
    (state) => state?.certifications?.details?.global
  );

  const mobileXlargeQuery = SCREEN_WIDTH_QUERIES.find(
    (item) => item.label === SCREEN_SIZE_MOBILE_XLARGE
  );
  const isMobileXlargeWidth = mobileXlargeQuery.value <= mediaQueryValue;

  return isMobileXlargeWidth ? (
    <div
      className='SaviCompanion-smartReviewWrapper'
      role='button'
      onClick={() => onClick(id, label)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          onClick(id, label);
        }
      }}
      tabIndex={tabIndex}
      id='SaviCompanion-smartReviewWrapper-mobile'>
      <div className='SaviCompanion-mobileWrapper'>
        <div className='SaviCompanion-smartDecision-illustrationWrapper'>
          <Illustration
            kind={iconKind}
            className='SaviCompanion-smartDecision-illustration'
          />
        </div>
        <div>
          {chipLabel && (
            <Chip label={chipLabel} kind='subtleFilled' color='info' />
          )}
          <Typography
            kind='h3'
            className={chipLabel && 'SaviCompanion-mobileWrapper-Header'}>
            <Color color='navy-900'>{label}</Color>
          </Typography>
        </div>
        <div className='SaviCompanion-smartDecision-Icon'>
          <Icon kind='ArrowRight' color='indigo-700' size='medium' />
        </div>
      </div>
      <Typography kind='body2'>{description}</Typography>
    </div>
  ) : (
    <div
      className='SaviCompanion-smartReviewWrapper'
      role='button'
      onClick={() => onClick(id, label)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          onClick(id, label);
        }
      }}
      tabIndex={tabIndex}>
      <div className='SaviCompanion-smartDecision-illustrationWrapper'>
        <Illustration
          kind={iconKind}
          className='SaviCompanion-smartDecision-illustration'
        />
      </div>
      <div>
        {chipLabel && (
          <Chip label={chipLabel} kind='subtleFilled' color='info' />
        )}
        <Typography kind='h3'>
          <Color color='navy-900'>{label}</Color>
        </Typography>
        <Typography kind='body2'>{description}</Typography>
      </div>
      <div className='SaviCompanion-smartDecision-Icon'>
        <Icon kind='ArrowRight' color='indigo-700' size='medium' />
      </div>
    </div>
  );
}

SaviStaticOption.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  iconKind: PropTypes.string.isRequired,
  chipLabel: PropTypes.string,
  onClick: PropTypes.func.isRequired,
  tabIndex: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

SaviStaticOption.defaultProps = {
  chipLabel: '',
  tabIndex: 0,
};

function SaviPopover({
  id,
  children,
  isPopoverOpen,
  openPopover,
  setSmartSelectedOption,
  manuallyReviewedDetails,
  setShowKeepDecisionText,
  disablePopup,
}) {
  const intl = useIntl();
  const { messages } = useSelector((state) => state?.user);

  const { mediaQueryValue } = useSelector(
    (state) => state?.certifications?.details?.global
  );

  const mobileXlargeQuery = SCREEN_WIDTH_QUERIES.find(
    (item) => item.label === SCREEN_SIZE_MOBILE_XLARGE
  );
  const isMobileXlargeWidth = mobileXlargeQuery.value <= mediaQueryValue;

  const l = useInvertedLocalize(invertedMessages);

  const onButtonClick = (overwriteFlag) => {
    openPopover(false, true);
    setSmartSelectedOption(id, 1, overwriteFlag);

    if (!overwriteFlag) {
      setShowKeepDecisionText(true);
    }
  };

  return (
    <Box tag='div' className='SaviCompanion-contextWrapper'>
      {!disablePopup ? (
        <>
          <Popover
            placement='right'
            trigger={children}
            popoverContent={
              <Box className='flexContainer'>
                <Typography kind='h3'>
                  {manuallyReviewedDetails.title}
                </Typography>
                <Typography kind='body2'>
                  {manuallyReviewedDetails.description}
                </Typography>
                <Box className='flexWrapper'>
                  <Button
                    type='button'
                    kind='ghost'
                    size='medium'
                    className='Overwrite'
                    onClick={() => onButtonClick(1)}>
                    <Typography kind='body3-bold' className='Text'>
                      {messages.CERTS_SAVI_OVERWRITE ||
                        localize(intl, getMessageKey('CERTS_SAVI_OVERWRITE'))}
                    </Typography>
                  </Button>
                  <Button
                    type='button'
                    kind='filled'
                    size='medium'
                    className='Keep'
                    onClick={() => onButtonClick(0)}>
                    <Typography kind='body3-bold' className='Text'>
                      {messages.CERTS_SAVI_KEEP_DECISIONS_LABEL ||
                        localize(
                          intl,
                          getMessageKey('CERTS_SAVI_KEEP_DECISIONS_LABEL')
                        )}
                    </Typography>
                  </Button>
                </Box>
              </Box>
            }
            isOpen={isPopoverOpen && !isMobileXlargeWidth}
            setIsOpen={openPopover}
          />
          {isMobileXlargeWidth && (
            <MobileDrawer
              content={
                <>
                  <header className='SaviCompanion-mobileDrawer-overwriteDecision-headerWrapper'>
                    <Box className='flexContainer' id='flexContainer'>
                      <Icon kind='SaviCompanionGradient' size='medium' />
                      <Typography kind='h3'>
                        {messages.CERTS_SAVI_KEEP_OR_OVERWRITE_LABEL ||
                          localize(
                            intl,
                            getMessageKey('CERTS_SAVI_KEEP_OR_OVERWRITE_LABEL')
                          )}
                      </Typography>
                    </Box>
                    <ButtonIcon
                      size='medium'
                      kind='ghost'
                      icon={
                        <Icon
                          kind='close'
                          color='neutral-100'
                          size='smallMedium'
                        />
                      }
                      onClick={() => openPopover(false, false)}
                      ariaLabel={l('Close')}
                    />
                  </header>
                  <Box className='flexContainer'>
                    <Typography kind='h3'>
                      {manuallyReviewedDetails.title}
                    </Typography>
                    <Typography kind='body2'>
                      {manuallyReviewedDetails.description}
                    </Typography>
                  </Box>
                  <Box className='flexContainer SaviCompanion-mobileDrawer-overwriteDecision-buttonsWrapper'>
                    <Button
                      type='button'
                      kind='ghost'
                      size='medium'
                      className='Overwrite'
                      onClick={() => onButtonClick(1)}>
                      <Typography
                        kind='body3-bold'
                        className='Text overwrite-Text'>
                        {messages.CERTS_SAVI_OVERWRITE ||
                          localize(intl, getMessageKey('CERTS_SAVI_OVERWRITE'))}
                      </Typography>
                    </Button>
                    <Button
                      type='button'
                      kind='filled'
                      size='medium'
                      className='Keep'
                      onClick={() => onButtonClick(0)}>
                      <Typography kind='body3-bold' className='Text keep-Text'>
                        {messages.CERTS_SAVI_KEEP_DECISIONS_LABEL ||
                          localize(
                            intl,
                            getMessageKey('CERTS_SAVI_KEEP_DECISIONS_LABEL')
                          )}
                      </Typography>
                    </Button>
                  </Box>
                </>
              }
              className='SaviCompanion-mobileDrawer-overwriteDecision'
              isOpen={isPopoverOpen}
              setIsOpen={openPopover}
            />
          )}
        </>
      ) : (
        children
      )}
    </Box>
  );
}

SaviPopover.propTypes = {
  id: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  isPopoverOpen: PropTypes.bool.isRequired,
  openPopover: PropTypes.func.isRequired,
  setSmartSelectedOption: PropTypes.func.isRequired,
  manuallyReviewedDetails: PropTypes.shape({
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
  }).isRequired,
  setShowKeepDecisionText: PropTypes.func.isRequired,
  disablePopup: PropTypes.bool.isRequired,
};

function SaviDynamicOption({
  id,
  label,
  description,
  iconKind,
  onClick,
  tabIndex,
  isManuallyReviewedItemsPresent,
  setSmartSelectedOption,
  manuallyReviewedDetails,
  isSaviActionsApplied,
  keepDecisionsLabel,
  isCheckBoxSelected,
  isKeepDecision,
  smartCancellationMessage,
  selectedSmartReviewOptions,
  enableCheckBox,
}) {
  const [isChecked, setIsChecked] = useState(isCheckBoxSelected === 1);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [showKeepDecisionText, setShowKeepDecisionText] = useState(
    isKeepDecision === 0
  );
  const [showSaviActionsApplied, setShowSaviActionsApplied] =
    useState(isSaviActionsApplied);

  const intl = useIntl();
  const { messages } = useSelector((state) => state?.user);

  const SAVI_DYNAMIC_OPTIONS_MAPPING = {
    savi_approve: {
      label:
        messages['Approve.Default.label.ATTESTATIONS'] ||
        localize(intl, getMessageKey('Approve.Default.label.ATTESTATIONS')),
      description:
        messages.CERTS_SAVI_APPROVE_MSG ||
        localize(intl, getMessageKey('CERTS_SAVI_APPROVE_MSG')),
      iconKind: 'ThumbUpHighScore',
      totalKey: 'savi_total_approve',
      actionTakenKey: 'savi_approved',
      manuallyReviewedMessageKeys: [
        {
          actionTakenKey: 'manually_approved',
          actionTakenLabel: 'approved',
        },
        {
          actionTakenKey: 'manually_revoked',
          actionTakenLabel: 'rejected',
        },
        {
          actionTakenKey: 'manually_enddate',
          actionTakenLabel: 'changed end date on',
        },
      ],
      manuallyReviewedActionConfirmation:
        messages.CERTS_SAVI_APPROVALS ||
        localize(intl, getMessageKey('CERTS_SAVI_APPROVALS')),
      applyActionKey: 'savi_approve',
      applyOverwriteKey: 'approve_overwrite',
    },
    savi_revoke: {
      label:
        messages.CERTS_REJECT_PRIMARY_BTN ||
        localize(intl, getMessageKey('CERTS_REJECT_PRIMARY_BTN')),
      description:
        messages.CERTS_SAVI_REVOKE_MSG ||
        localize(intl, getMessageKey('CERTS_SAVI_REVOKE_MSG')),
      iconKind: 'ThumbDownLowScore',
      totalKey: 'savi_total_revoke',
      actionTakenKey: 'savi_revoked',
      manuallyReviewedMessageKeys: [
        {
          actionTakenKey: 'manually_approved',
          actionTakenLabel: 'approved',
        },
        {
          actionTakenKey: 'manually_revoked',
          actionTakenLabel: 'rejected',
        },
        {
          actionTakenKey: 'manually_enddate',
          actionTakenLabel: 'changed end date on',
        },
      ],
      manuallyReviewedActionConfirmation:
        messages.CERTS_SAVI_REJECTIONS ||
        localize(intl, getMessageKey('CERTS_SAVI_REJECTIONS')),
      applyActionKey: 'savi_reject',
      applyOverwriteKey: 'reject_overwrite',
    },
  };

  const { mediaQueryValue } = useSelector(
    (state) => state?.certifications?.details?.global
  );

  const mobileXlargeQuery = SCREEN_WIDTH_QUERIES.find(
    (item) => item.label === SCREEN_SIZE_MOBILE_XLARGE
  );
  const isMobileXlargeWidth = mobileXlargeQuery.value <= mediaQueryValue;

  const showCancelConfirmMessage =
    selectedSmartReviewOptions[
      SAVI_DYNAMIC_OPTIONS_MAPPING[id].applyActionKey
    ] === 0;

  const onOptionClick = (event) => {
    const isLocalChecked = !isChecked;

    setIsChecked(isLocalChecked);
    onClick(id, isLocalChecked, isManuallyReviewedItemsPresent);
    setShowKeepDecisionText(false);
    event.preventDefault();
  };

  const openPopover = (open, isButtonClicked) => {
    let localIsChecked = isChecked;

    if (!open && !(isButtonClicked === true)) {
      localIsChecked = false;
      setIsChecked(localIsChecked);
    }

    setIsPopoverOpen(open && !localIsChecked);
  };

  const onCancelSaviActions = () => {
    setShowSaviActionsApplied(false);
    setSmartSelectedOption(id, 0);

    return false;
  };

  return (
    <SaviPopover
      id={id}
      isPopoverOpen={isManuallyReviewedItemsPresent ? isPopoverOpen : false}
      openPopover={openPopover}
      setSmartSelectedOption={setSmartSelectedOption}
      manuallyReviewedDetails={manuallyReviewedDetails}
      setShowKeepDecisionText={setShowKeepDecisionText}
      disablePopup={isSaviActionsApplied}>
      <div
        className='SaviCompanion-smartReviewWrapper'
        id={
          isMobileXlargeWidth &&
          showSaviActionsApplied &&
          !showCancelConfirmMessage &&
          'SaviCompanion-smartReviewWrapper-mobile'
        }
        key={id}
        role='button'
        onClick={
          !showSaviActionsApplied && !showCancelConfirmMessage && enableCheckBox
            ? onOptionClick
            : undefined
        }
        onKeyDown={(e) => {
          if (
            e.key === 'Enter' &&
            !showSaviActionsApplied &&
            !showCancelConfirmMessage &&
            enableCheckBox
          ) {
            onOptionClick();
          }
        }}
        tabIndex={tabIndex}>
        {isMobileXlargeWidth &&
          showSaviActionsApplied &&
          !showCancelConfirmMessage && (
            <div className='Applied-Savi'>
              <Icon kind='CheckCircleOutline' size='xSmall' />
              <Typography kind='body3'>
                {messages.CERTS_SAVI_APPLIED ||
                  localize(intl, getMessageKey('CERTS_SAVI_APPLIED'))}
              </Typography>
            </div>
          )}
        <div className='SaviCompanion-smartDecision-wrapper'>
          <div className='SaviCompanion-smartDecision-Icon'>
            <Illustration
              kind={iconKind}
              className='SaviCompanion-smartDecision-illustrationXLarge'
            />
            <Box className='DisplayFlex-Column'>
              <Typography kind='h3'>
                <Color color='navy-900'>
                  {showKeepDecisionText ? keepDecisionsLabel : label}
                </Color>
              </Typography>
              {showKeepDecisionText && (
                <Typography kind='body2'>
                  <Color color='navy-900'>
                    {manuallyReviewedDetails.overwriteDecision}
                  </Color>
                </Typography>
              )}
            </Box>
            {!isMobileXlargeWidth &&
              showSaviActionsApplied &&
              !showCancelConfirmMessage && (
                <div className='Applied-Savi'>
                  <Icon kind='CheckCircleOutline' size='xSmall' />
                  <Typography kind='body3'>
                    {messages.CERTS_SAVI_APPLIED ||
                      localize(intl, getMessageKey('CERTS_SAVI_APPLIED'))}
                  </Typography>
                </div>
              )}
          </div>
          <div>
            <Typography kind='body2'>{description}</Typography>
          </div>
          <div>
            {showCancelConfirmMessage && (
              <InlineMessage
                colorTheme='info'
                text={smartCancellationMessage}
                leftIcon={<Icon kind='AlertCriticalFilled' />}
              />
            )}
          </div>
          {showSaviActionsApplied && !showCancelConfirmMessage && (
            <div className='SaviCompanion-cancelSmartApprovals'>
              <Button
                type='button'
                kind='subtle'
                size='large'
                onClick={onCancelSaviActions}>
                <Typography kind='body3-bold' className='Text'>
                  {manuallyReviewedDetails.cancelLabel}
                </Typography>
              </Button>
            </div>
          )}
        </div>
        {(!showSaviActionsApplied || showCancelConfirmMessage) && (
          <div className='SaviCompanion-smartDecision-Icon'>
            <Checkbox
              size='medium'
              isChecked={isChecked}
              tabIndex={-1}
              onChange={undefined}
              isDisabled={
                showSaviActionsApplied ||
                showCancelConfirmMessage ||
                !enableCheckBox
              }
            />
          </div>
        )}
      </div>
    </SaviPopover>
  );
}

SaviDynamicOption.propTypes = {
  id: PropTypes.number.isRequired,
  iconKind: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  smartCancellationMessage: PropTypes.string.isRequired,
  isSaviActionsApplied: PropTypes.bool.isRequired,
  selectedSmartReviewOptions: PropTypes.shape({}).isRequired,
  onClick: PropTypes.func.isRequired,
  isCheckBoxSelected: PropTypes.number.isRequired,
  isManuallyReviewedItemsPresent: PropTypes.bool.isRequired,
  manuallyReviewedDetails: PropTypes.shape({
    overwriteDecision: PropTypes.string,
    cancelLabel: PropTypes.string,
  }).isRequired,
  setSmartSelectedOption: PropTypes.func.isRequired,
  isKeepDecision: PropTypes.number.isRequired,
  keepDecisionsLabel: PropTypes.string.isRequired,
  tabIndex: PropTypes.number,
};

SaviDynamicOption.defaultProps = {
  tabIndex: 0,
};

function Divider() {
  return <div className='Savi-Companion-Divider' />;
}

function SmartReview({
  title,
  children,
  isOptionSelected,
  selectedOption,
  setIsOptionSelected,
}) {
  return (
    <div className='SaviCompanionBody-Wrapper'>
      <QuestionHeader
        isOptionSelected={isOptionSelected}
        minimiseReason={selectedOption}
        title={title}
        setIsOptionSelected={setIsOptionSelected}
      />
      {!isOptionSelected && children}
    </div>
  );
}

SmartReview.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  isOptionSelected: PropTypes.bool.isRequired,
  selectedOption: PropTypes.string,
  setIsOptionSelected: PropTypes.func.isRequired,
};

SmartReview.defaultProps = {
  selectedOption: null,
};

function LoaderComponent({ text }) {
  return (
    <div className='Savi-Companion-Loader-Wrapper'>
      <div className='Savi-Companion-Loader-Body'>
        <Typography kind='body2' className='Savi-Companion-Loader-Text'>
          {text}
        </Typography>
        <Loader
          className='Savi-Companion-Loader-Setting'
          kind='loop'
          format='inline'
          color='brand'
          size='large'
        />
      </div>
    </div>
  );
}

LoaderComponent.propTypes = {
  text: PropTypes.string.isRequired,
};

function ErrorComponent({ errorText, retryAction, retryText }) {
  return (
    <div className='Savi-Companion-Loader-Wrapper'>
      <div className='Savi-Companion-Loader-Body'>
        <div className='AlertText'>
          <Icon kind='AlertCriticalOutline' color='critical-500' size='small' />
          <Typography kind='body2' className='Savi-Companion-Error-Text'>
            {errorText}
          </Typography>
        </div>
        <Button
          onClick={retryAction}
          kind='ghost'
          size='medium'
          rightIcon={<Icon kind='Retry' size='small' />}>
          {retryText}
        </Button>
      </div>
    </div>
  );
}

ErrorComponent.propTypes = {
  errorText: PropTypes.string.isRequired,
  retryAction: PropTypes.func.isRequired,
};

function NothingToShow({ emptyMsg }) {
  return (
    <div className='Savi-Companion-Loader-Wrapper'>
      <div>
        <Illustration kind='Empty' className='Savi-Companion-illustration' />
      </div>
      <Typography kind='body2' className='Savi-Companion-Loader-Text'>
        {emptyMsg || ' '}
      </Typography>
    </div>
  );
}

function SaviCompanionBody({
  setSelectedSmartReviewOptions,
  selectedSmartReviewOptions,
  isLoading,
  isSaviActionsTaken,
  smartReviewOptions,
  setShowSaviCompanion,
  isApplyActionLoading,
  errorDetails,
  isCertificationActionable,
}) {
  const [isOptionSelected, setIsOptionSelected] = useState(false);
  const [selectedOption, setSelectedOption] = useState('');
  const [isDynamicOptionSelected, setIsDynamicOptionSelected] = useState(false);
  const [dynamicSelectedOption, setDynamicSelectedOption] = useState([]);

  const [isShowSaviViewEnabled, setIsShowSaviViewEnabled] = useState(true);

  const intl = useIntl();
  const { messages } = useSelector((state) => state?.user);

  const l = useInvertedLocalize(invertedMessages);

  const {
    saviCompanion: { statusDetails: { smartActionDetails } = {} },
    reviewAndSubmit: { certificationStatus },
  } = useSelector((state) => state?.certifications?.details);

  // static options

  const SAVI_STATIC_OPTION = [
    {
      id: 'smart_decisions',
      label:
        messages.CERTS_SAVI_MAKE_SMART_DECISIONS ||
        localize(intl, getMessageKey('CERTS_SAVI_MAKE_SMART_DECISIONS')),
      description:
        messages.CERTS_SAVI_TRUST_RISK_RECOMMENDATION ||
        localize(intl, getMessageKey('CERTS_SAVI_TRUST_RISK_RECOMMENDATION')),
      iconKind: 'Prioritize',
      isRecommended: true,
      tabIndex: -1,
    },
    {
      id: 'own_review',
      label:
        messages.CERTS_SAVI_REVIEW_ON_OWN ||
        localize(intl, getMessageKey('CERTS_SAVI_REVIEW_ON_OWN')),
      description:
        messages.CERTS_SAVI_REVIEW_MSG ||
        localize(intl, getMessageKey('CERTS_SAVI_REVIEW_MSG')),
      iconKind: 'SelfReview',
      isRecommended: false,
      tabIndex: 0,
    },
  ];

  const SAVI_DYNAMIC_OPTIONS_MAPPING = {
    savi_approve: {
      label:
        messages['Approve.Default.label.ATTESTATIONS'] ||
        localize(intl, getMessageKey('Approve.Default.label.ATTESTATIONS')),
      description:
        messages.CERTS_SAVI_APPROVE_MSG ||
        localize(intl, getMessageKey('CERTS_SAVI_APPROVE_MSG')),
      iconKind: 'ThumbUpHighScore',
      totalKey: 'savi_total_approve',
      actionTakenKey: 'savi_approved',
      manuallyReviewedMessageKeys: [
        {
          actionTakenKey: 'manually_approved',
          actionTakenLabel: 'approved',
        },
        {
          actionTakenKey: 'manually_revoked',
          actionTakenLabel: 'rejected',
        },
        {
          actionTakenKey: 'manually_enddate',
          actionTakenLabel: 'changed end date on',
        },
      ],
      manuallyReviewedActionConfirmation:
        messages.CERTS_SAVI_APPROVALS ||
        localize(intl, getMessageKey('CERTS_SAVI_APPROVALS')),
      applyActionKey: 'savi_approve',
      applyOverwriteKey: 'approve_overwrite',
    },
    savi_revoke: {
      label:
        messages.CERTS_REJECT_PRIMARY_BTN ||
        localize(intl, getMessageKey('CERTS_REJECT_PRIMARY_BTN')),
      description:
        messages.CERTS_SAVI_REVOKE_MSG ||
        localize(intl, getMessageKey('CERTS_SAVI_REVOKE_MSG')),
      iconKind: 'ThumbDownLowScore',
      totalKey: 'savi_total_revoke',
      actionTakenKey: 'savi_revoked',
      manuallyReviewedMessageKeys: [
        {
          actionTakenKey: 'manually_approved',
          actionTakenLabel: 'approved',
        },
        {
          actionTakenKey: 'manually_revoked',
          actionTakenLabel: 'rejected',
        },
        {
          actionTakenKey: 'manually_enddate',
          actionTakenLabel: 'changed end date on',
        },
      ],
      manuallyReviewedActionConfirmation:
        messages.CERTS_SAVI_REJECTIONS ||
        localize(intl, getMessageKey('CERTS_SAVI_REJECTIONS')),
      applyActionKey: 'savi_reject',
      applyOverwriteKey: 'reject_overwrite',
    },
  };

  useEffect(() => {
    if (smartActionDetails) {
      const { savi_approve: saviApprove, savi_revoke: saviRevoke } =
        smartActionDetails;

      if (
        saviApprove &&
        saviRevoke &&
        typeof saviApprove === 'object' &&
        typeof saviRevoke === 'object'
      ) {
        const isSmartActionExist =
          Object.keys(saviApprove)?.length > 0 &&
          Object.keys(saviRevoke)?.length > 0;

        setIsShowSaviViewEnabled(isSmartActionExist);
      }
    }
  }, [smartActionDetails]);

  useEffect(() => {
    if (smartReviewOptions.length > 0) {
      const dynamicOptions = [];

      smartReviewOptions.map((item) => dynamicOptions.push(item.label));
      setDynamicSelectedOption(dynamicOptions);
    }
  }, [smartReviewOptions]);

  const onOptionSelected = (id, reason) => {
    if (id === 'smart_decisions') {
      setIsOptionSelected(true);
      setSelectedOption(reason);
    } else {
      setShowSaviCompanion(false);
    }
  };

  const setSmartSelectedOption = (itemKey, value, overwriteFlag) => {
    const optionKey = SAVI_DYNAMIC_OPTIONS_MAPPING[itemKey].applyActionKey;
    const overwriteOptionKey =
      SAVI_DYNAMIC_OPTIONS_MAPPING[itemKey].applyOverwriteKey;
    const { [optionKey]: selectedItem } = selectedSmartReviewOptions;
    const selectedOptions = { ...selectedSmartReviewOptions };

    if (selectedItem) {
      delete selectedOptions[optionKey];
      delete selectedOptions[overwriteOptionKey];
    } else {
      selectedOptions[optionKey] = value;
      selectedOptions[overwriteOptionKey] = overwriteFlag;
    }

    setSelectedSmartReviewOptions(selectedOptions);
  };

  const onDynamicOptionClick = (
    itemKey,
    isChecked,
    isManuallyReviewedItemsPresent
  ) => {
    if (!isChecked || !isManuallyReviewedItemsPresent) {
      setSmartSelectedOption(itemKey, 1);
    }
  };

  const dynamicActionsBody = () => (
    <>
      {!isSaviActionsTaken ? (
        <SmartReview
          title={
            messages.CERTS_SAVI_HELP_MSG ||
            localize(intl, getMessageKey('CERTS_SAVI_HELP_MSG'))
          }
          isOptionSelected={isOptionSelected}
          selectedOption={selectedOption}
          setIsOptionSelected={setIsOptionSelected}>
          <div className='Options-Wrapper'>
            {SAVI_STATIC_OPTION.map(
              (
                { id, label, description, iconKind, isRecommended, tabIndex },
                index
              ) => (
                <React.Fragment key={id}>
                  <SaviStaticOption
                    id={id}
                    label={label}
                    description={description}
                    iconKind={iconKind}
                    onClick={onOptionSelected}
                    tabIndex={tabIndex}
                    chipLabel={isRecommended ? l('Recommended') : null}
                  />
                  {index < SAVI_STATIC_OPTION.length - 1 && <Divider />}
                </React.Fragment>
              )
            )}
          </div>
        </SmartReview>
      ) : null}
      {(selectedOption || isSaviActionsTaken) && (
        <span>
          {isApplyActionLoading ? (
            <LoaderComponent
              text={
                messages.CERTS_SAVI_LOADING_MSG ||
                localize(intl, getMessageKey('CERTS_SAVI_LOADING_MSG'))
              }
            />
          ) : (
            <SmartReview
              title={
                messages.CERTS_SAVI_QUESTION_LABEL ||
                localize(intl, getMessageKey('CERTS_SAVI_QUESTION_LABEL'))
              }
              isOptionSelected={isDynamicOptionSelected}
              selectedOption={dynamicSelectedOption.join(', ')}
              setIsOptionSelected={setIsDynamicOptionSelected}>
              <div className='Options-Wrapper'>
                {smartReviewOptions.map(
                  ({
                    id,
                    label,
                    description,
                    iconKind,
                    isManuallyReviewedItemsPresent,
                    manuallyReviewedDetails,
                    isSaviActionsApplied,
                    keepDecisionsLabel,
                    actionKey,
                    applyOverwriteKey,
                    smartCancellationMessage,
                  }) => (
                    <SaviDynamicOption
                      key={id}
                      id={id}
                      label={label}
                      description={description}
                      iconKind={iconKind}
                      onClick={onDynamicOptionClick}
                      isManuallyReviewedItemsPresent={
                        isManuallyReviewedItemsPresent
                      }
                      setSmartSelectedOption={setSmartSelectedOption}
                      manuallyReviewedDetails={manuallyReviewedDetails}
                      isSaviActionsApplied={isSaviActionsApplied}
                      keepDecisionsLabel={keepDecisionsLabel}
                      isCheckBoxSelected={selectedSmartReviewOptions[actionKey]}
                      isKeepDecision={
                        selectedSmartReviewOptions[applyOverwriteKey]
                      }
                      smartCancellationMessage={smartCancellationMessage}
                      selectedSmartReviewOptions={selectedSmartReviewOptions}
                      enableCheckBox={
                        certificationStatus !==
                        CERTIFICATION_PREVEIW_STATUS_CODE
                      }
                    />
                  )
                )}
              </div>
            </SmartReview>
          )}
        </span>
      )}
    </>
  );

  if (!isCertificationActionable && !isShowSaviViewEnabled) {
    return (
      <NothingToShow
        emptyMsg={
          messages.CERTS_SAVI_EMPTY_MSG ||
          localize(intl, getMessageKey('CERTS_SAVI_EMPTY_MSG'))
        }
      />
    );
  }

  if (errorDetails && errorDetails.title) {
    return (
      <ErrorComponent
        errorText={errorDetails.title}
        retryAction={errorDetails.retryAction}
      />
    );
  }

  if (isLoading) {
    return (
      <LoaderComponent
        text={
          messages.CERTS_SAVI_LOADING_MSG ||
          localize(intl, getMessageKey('CERTS_SAVI_LOADING_MSG'))
        }
      />
    );
  }

  if (smartReviewOptions.length === 0) {
    return (
      <NothingToShow
        emptyMsg={
          messages.CERTS_SAVI_EMPTY_MSG ||
          localize(intl, getMessageKey('CERTS_SAVI_EMPTY_MSG'))
        }
      />
    );
  }

  return dynamicActionsBody();
}

SaviCompanionBody.propTypes = {
  smartReviewOptions: PropTypes.arrayOf(PropTypes.shape({})),
  setSelectedSmartReviewOptions: PropTypes.func,
  selectedSmartReviewOptions: PropTypes.shape({}),
  isApplyActionLoading: PropTypes.bool,
  isSaviActionsTaken: PropTypes.bool,
  isCertificationActionable: PropTypes.bool,
  setShowSaviCompanion: PropTypes.func,
  isLoading: PropTypes.bool,
  errorDetails: PropTypes.shape({
    title: PropTypes.string,
    retryAction: PropTypes.func,
  }),
  onboardingList: PropTypes.func,
  importSaviyntExAppClear: PropTypes.func,
};

SaviCompanionBody.defaultProps = {
  smartReviewOptions: [],
  setSelectedSmartReviewOptions: () => {},
  selectedSmartReviewOptions: {},
  isApplyActionLoading: null,
  isSaviActionsTaken: null,
  isCertificationActionable: null,
  setShowSaviCompanion: () => {},
  isLoading: null,
  errorDetails: PropTypes.shape({
    title: null,
    retryAction: () => {},
  }),
  onboardingList: () => {},
  importSaviyntExAppClear: () => {},
};

const getDescriptionFromManuallyReviewedItems = (
  actionDetails,
  config,
  messages,
  intl
) => {
  const actionArray = [];

  config.manuallyReviewedMessageKeys.map((item) => {
    const actionCount = actionDetails[item.actionTakenKey];

    if (actionCount > 0) {
      actionArray.push(
        `${item.actionTakenLabel} ${actionCount} ${pluralizeString(
          'item',
          actionCount > 1
        )}`
      );
    }

    return item;
  });

  return certsFormattedMessage(
    messages.CERTS_SAVI_REVIEW_TEXT ||
      localize(intl, getMessageKey('CERTS_SAVI_REVIEW_TEXT')),
    {
      details: actionArray.join(', and '),
      label: config.label.toLowerCase(),
      action: config.manuallyReviewedActionConfirmation.toLowerCase(),
    }
  );
};

const processSmartActionDetails = (
  saviDynamicOptionsMappings,
  actionDetails,
  messages,
  intl,
  l
) => {
  const newSmartReviewOptions = [];
  let saviActionTakenCount = 0;

  Object.keys(actionDetails).map((item) => {
    const config = saviDynamicOptionsMappings[item];
    const manuallyReviewedTotal =
      actionDetails[item].manually_approved +
      actionDetails[item].manually_revoked +
      actionDetails[item].manually_enddate;
    const isSaviActionsTaken = actionDetails[item][config.actionTakenKey] > 0;
    const totalSmartActionsCount =
      actionDetails[item][
        isSaviActionsTaken ? config.actionTakenKey : config.totalKey
      ] || 0;
    const keepDecisionsTotal =
      (totalSmartActionsCount || 0) - (manuallyReviewedTotal || 0);

    if (totalSmartActionsCount > 0) {
      newSmartReviewOptions.push({
        id: item,
        label: l(`Smart {action} {count} {pluralizeItems}`, {
          action: config.label,
          count: totalSmartActionsCount,
          pluralizeItems: pluralizeString('Item', totalSmartActionsCount > 1),
        }),
        keepDecisionsLabel: l(`Smart {action} {count} {pluralizeItems}`, {
          action: config.label,
          count: keepDecisionsTotal,
          pluralizeItems: pluralizeString('Item', keepDecisionsTotal > 1),
        }),
        description: config.description,
        iconKind: config.iconKind,
        isManuallyReviewedItemsPresent: manuallyReviewedTotal > 0,
        isSaviActionsApplied: isSaviActionsTaken,
        smartCancellationMessage: l(
          'All Smart {reviewedActionConfirmation} will be cancelled',
          {
            reviewedActionConfirmation:
              config.manuallyReviewedActionConfirmation,
          }
        ),
        totalSmartActionsCount,
        manuallyReviewedDetails: {
          title: l(
            'You have manually reviewed {manuallyReviewedTotal} {pluralizeItems}',
            {
              manuallyReviewedTotal,
              pluralizeItems: pluralizeString(
                'item',
                manuallyReviewedTotal > 1
              ),
            }
          ),
          description: getDescriptionFromManuallyReviewedItems(
            actionDetails[item],
            config,
            messages,
            intl
          ),
          overwriteDecision: l(
            'Keep Your {manuallyReviewedTotal} {pluralizeItems}',
            {
              manuallyReviewedTotal,
              pluralizeItems: pluralizeString(
                'Decision',
                manuallyReviewedTotal > 1
              ),
            }
          ),
          cancelLabel: l('Cancel Smart {manuallyReviewedActionConfirmation}', {
            manuallyReviewedActionConfirmation:
              config.manuallyReviewedActionConfirmation,
          }),
        },
        actionKey: config.applyActionKey,
        applyOverwriteKey: config.applyOverwriteKey,
      });
    }

    saviActionTakenCount += actionDetails[item][config.actionTakenKey];

    return item;
  });

  return {
    newSmartReviewOptions,
    saviActionTakenCount,
  };
};

function SaviCompanion({ certKey, setShowSaviCompanion }) {
  const dispatch = useDispatch();

  const [isProceedWithSavi, setIsProceedWithSavi] = useState(false);
  const [smartReviewOptions, setSmartReviewOptions] = useState([]);
  const [selectedSmartReviewOptions, setSelectedSmartReviewOptions] = useState(
    {}
  );
  const [isSaviActionsTaken, setIsSaviActionsTaken] = useState(false);
  const showIntroLocal = cookies.get('show_savi_intro');
  const [showSaviCompanionIntro, setShowSaviCompanionIntro] = useState(
    !showIntroLocal || showIntroLocal === '1'
  );
  const [errorDetails, setErrorDetails] = useState({});
  const intl = useIntl();
  const { messages } = useSelector((state) => state?.user);
  const {
    core: { columnNames },
    global: { mediaQueryValue },
    saviCompanion: {
      isApplyActionLoading,
      isCertificationActionable,
      showIntro,
      statusDetails: { smartActionDetails, loading } = {},
    },
    reviewAndSubmit: { certificationStatus },
    tabs: { tabData },
  } = useSelector((state) => state?.certifications?.details);

  const l = useInvertedLocalize(invertedMessages);

  const mobileXlargeQuery = SCREEN_WIDTH_QUERIES.find(
    (item) => item.label === SCREEN_SIZE_MOBILE_XLARGE
  );
  const isMobileXlargeWidth = mobileXlargeQuery.value <= mediaQueryValue;

  const SAV_COMPANION_TITLE_KIND = {
    landing: (
      <span>
        {messages.CERTS_SAVI_HI_MSGG ||
          localize(intl, getMessageKey('CERTS_SAVI_HI_MSG'))}
        &nbsp;
        <span className='Savi-highlight'>
          {messages.CERTS_SAVI_BRAND_NAME ||
            localize(intl, getMessageKey('CERTS_SAVI_BRAND_NAME'))}
        </span>
        ,
        {messages.CERTS_SAVI_AI_COMPANION_LABEL ||
          localize(intl, getMessageKey('CERTS_SAVI_AI_COMPANION_LABEL'))}
      </span>
    ),
    normal: (
      <span className='Savi-highlight'>
        {messages.CERTS_SAVI_RECOMMENDATIONS ||
          localize(intl, getMessageKey('CERTS_SAVI_RECOMMENDATIONS'))}
      </span>
    ),
  };

  const SAVI_DYNAMIC_OPTIONS_MAPPING = {
    savi_approve: {
      label:
        messages['Approve.Default.label.ATTESTATIONS'] ||
        localize(intl, getMessageKey('Approve.Default.label.ATTESTATIONS')),
      description:
        messages.CERTS_SAVI_APPROVE_MSG ||
        localize(intl, getMessageKey('CERTS_SAVI_APPROVE_MSG')),
      iconKind: 'ThumbUpHighScore',
      totalKey: 'savi_total_approve',
      actionTakenKey: 'savi_approved',
      manuallyReviewedMessageKeys: [
        {
          actionTakenKey: 'manually_approved',
          actionTakenLabel: 'approved',
        },
        {
          actionTakenKey: 'manually_revoked',
          actionTakenLabel: 'rejected',
        },
        {
          actionTakenKey: 'manually_enddate',
          actionTakenLabel: 'changed end date on',
        },
      ],
      manuallyReviewedActionConfirmation:
        messages.CERTS_SAVI_APPROVALS ||
        localize(intl, getMessageKey('CERTS_SAVI_APPROVALS')),
      applyActionKey: 'savi_approve',
      applyOverwriteKey: 'approve_overwrite',
    },
    savi_revoke: {
      label:
        messages.CERTS_REJECT_PRIMARY_BTN ||
        localize(intl, getMessageKey('CERTS_REJECT_PRIMARY_BTN')),
      description:
        messages.CERTS_SAVI_REVOKE_MSG ||
        localize(intl, getMessageKey('CERTS_SAVI_REVOKE_MSG')),
      iconKind: 'ThumbDownLowScore',
      totalKey: 'savi_total_revoke',
      actionTakenKey: 'savi_revoked',
      manuallyReviewedMessageKeys: [
        {
          actionTakenKey: 'manually_approved',
          actionTakenLabel: 'approved',
        },
        {
          actionTakenKey: 'manually_revoked',
          actionTakenLabel: 'rejected',
        },
        {
          actionTakenKey: 'manually_enddate',
          actionTakenLabel: 'changed end date on',
        },
      ],
      manuallyReviewedActionConfirmation:
        messages.CERTS_SAVI_REJECTIONS ||
        localize(intl, getMessageKey('CERTS_SAVI_REJECTIONS')),
      applyActionKey: 'savi_reject',
      applyOverwriteKey: 'reject_overwrite',
    },
  };

  const SaviCompanionTitle = ({ kind }) => SAV_COMPANION_TITLE_KIND[kind];

  const getSaviStatus = () => {
    dispatch(getSaviCompanionStatus(certKey, setErrorDetails, getSaviStatus));
  };

  const mobileSaviFooterHeight =
    isMobileXlargeWidth &&
    (Object.keys(selectedSmartReviewOptions).length > 0
      ? 'SaviCompanion-mobile-isSeeResults'
      : 'SaviCompanion-mobile-isNotSeeResults');
  const saviModalClasses = classnames(
    'SaviCompanion-modal',
    `SaviCompanion-main${isProceedWithSavi}`,
    mobileSaviFooterHeight
  );

  useEffect(() => {
    if (
      isCertificationActionable ||
      certificationStatus === CERTIFICATION_PREVEIW_STATUS_CODE
    ) {
      getSaviStatus();
    }
  }, [isCertificationActionable]);

  useEffect(() => {
    if (smartActionDetails) {
      const { newSmartReviewOptions, saviActionTakenCount } =
        processSmartActionDetails(
          SAVI_DYNAMIC_OPTIONS_MAPPING,
          smartActionDetails,
          messages,
          intl,
          l
        );

      setSmartReviewOptions(newSmartReviewOptions);
      setIsSaviActionsTaken(saviActionTakenCount > 0);
      setIsProceedWithSavi(saviActionTakenCount > 0);
    }
  }, [smartActionDetails]);

  const closeSaviPopup = () => {
    setShowSaviCompanion(false);
    setSmartReviewOptions([]);
    setSelectedSmartReviewOptions({});
  };

  const onApplySaviActions = () => {
    // to reset the items list after  savi taking action
    dispatch(setCertTriggerFromSearchOrFilter(true));
    dispatch(
      applySaviCompanionActions(
        {
          certkey: certKey,
          signal: 'TRUST_SCORE',
          ...selectedSmartReviewOptions,
        },
        {
          columnNames,
          max: MAX_OFFSET,
          offset: 0,
          tabData,
          postSuccessAction: closeSaviPopup,
          postFailureAction: setErrorDetails,
          retryAction: onApplySaviActions,
        }
      )
    );
  };

  const onGotItClicked = () => {
    setIsProceedWithSavi(true);
    setShowSaviCompanionIntro(false);
    cookies.set('show_savi_intro', '0');
  };

  return (
    <ModalIsland
      key='saviCompanionModal'
      kind='default'
      onClose={() => setShowSaviCompanion(false)}
      className={saviModalClasses}
      isOpen
      headerIcon={<Icon kind='SaviCompanionGradient' size='large' />}
      title={
        <SaviCompanionTitle
          kind={!showSaviCompanionIntro ? 'normal' : 'landing'}
        />
      }
      contentBody={
        <>
          {showSaviCompanionIntro && (
            <SaviCompanionLandingBody onGotItClick={onGotItClicked} />
          )}
          {(!showIntro || isProceedWithSavi) && (
            <SaviCompanionBody
              setSelectedSmartReviewOptions={setSelectedSmartReviewOptions}
              selectedSmartReviewOptions={selectedSmartReviewOptions}
              isLoading={loading}
              isSaviActionsTaken={isSaviActionsTaken}
              smartReviewOptions={smartReviewOptions}
              setShowSaviCompanion={setShowSaviCompanion}
              isApplyActionLoading={isApplyActionLoading}
              errorDetails={errorDetails}
              isCertificationActionable={isCertificationActionable}
            />
          )}
        </>
      }
      FooterActionBarComp={
        Object.keys(selectedSmartReviewOptions).length > 0 ? (
          <div className='SaviCompanion-Footer'>
            <Button
              type='button'
              kind='filled'
              size='large'
              className='ApplyActionButton'
              onClick={onApplySaviActions}>
              <Typography kind='body2-bold' className='Text'>
                {messages.CERTS_SAVI_APPLY ||
                  localize(intl, getMessageKey('CERTS_SAVI_APPLY'))}
              </Typography>
            </Button>
          </div>
        ) : null
      }
    />
  );
}

SaviCompanion.propTypes = {
  certKey: PropTypes.string.isRequired,
  setShowSaviCompanion: PropTypes.func.isRequired,
};

export default SaviCompanion;
